import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, X, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

const Cart = () => {
  const { items, updateQty, removeItem, subtotal } = useCart();
  const [email, setEmail] = useState("");
  const navigate = useNavigate();

  const checkout = () => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email for delivery.");
      return;
    }
    navigate("/checkout", { state: { email } });
  };

  if (items.length === 0) {
    return (
      <div className="pt-40 pb-24 text-center" data-testid="cart-empty">
        <ShoppingBag className="w-10 h-10 text-[#C6A769] mx-auto mb-6" strokeWidth={1.2} />
        <h1 className="font-serif-display text-4xl text-[#4A3F35]">Your cart is empty</h1>
        <p className="text-[#8A7D71] mt-3">Let's find something worth investing in.</p>
        <Link
          to="/shop"
          data-testid="empty-cart-shop-link"
          className="mt-8 inline-block bg-[#4A3F35] text-[#FAF7F2] px-9 py-4 rounded-sm text-sm uppercase tracking-[0.15em] hover:bg-[#C6A769] transition-colors duration-500"
        >
          Shop the Collection
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="pt-32 pb-10">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <h1 className="font-serif-display text-5xl text-[#4A3F35] mb-12">Your Cart</h1>

        <div className="grid lg:grid-cols-3 gap-14">
          <div className="lg:col-span-2 space-y-8">
            {items.map((item) => (
              <div
                key={item.id}
                data-testid={`cart-item-${item.id}`}
                className="flex gap-5 pb-8 border-b border-[#4A3F35]/10"
              >
                <Link to={`/product/${item.id}`} className="w-24 h-28 shrink-0 overflow-hidden rounded-sm bg-[#FAF7F2]">
                  <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                </Link>
                <div className="flex-1">
                  <div className="flex justify-between gap-4">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-[#C6A769]">{item.category}</p>
                      <h3 className="font-serif-display text-xl text-[#4A3F35]">{item.name}</h3>
                    </div>
                    <button
                      onClick={() => removeItem(item.id)}
                      data-testid={`remove-item-${item.id}`}
                      className="text-[#8A7D71] hover:text-[#4A3F35] h-fit"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between mt-5">
                    <div className="flex items-center border border-[#4A3F35]/20 rounded-sm">
                      <button
                        onClick={() => updateQty(item.id, item.quantity - 1)}
                        data-testid={`decrease-qty-${item.id}`}
                        className="px-3 py-2 hover:text-[#C6A769]"
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className="px-4 text-sm" data-testid={`qty-${item.id}`}>{item.quantity}</span>
                      <button
                        onClick={() => updateQty(item.id, item.quantity + 1)}
                        data-testid={`increase-qty-${item.id}`}
                        className="px-3 py-2 hover:text-[#C6A769]"
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <p className="font-serif-display text-xl text-[#4A3F35]">
                      ${(item.price * item.quantity).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-[#FAF7F2] rounded-sm p-8 h-fit lg:sticky lg:top-28" data-testid="order-summary">
            <h2 className="font-serif-display text-2xl text-[#4A3F35] mb-6">Order Summary</h2>
            <div className="flex justify-between text-sm text-[#8A7D71] mb-3">
              <span>Subtotal</span>
              <span data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm text-[#8A7D71] mb-5 pb-5 border-b border-[#4A3F35]/10">
              <span>Delivery</span>
              <span>Instant · Free</span>
            </div>
            <div className="flex justify-between font-serif-display text-2xl text-[#4A3F35] mb-7">
              <span>Total</span>
              <span data-testid="cart-total">${subtotal.toFixed(2)}</span>
            </div>

            <label className="text-xs uppercase tracking-[0.18em] text-[#4A3F35] block mb-2">
              Delivery Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              data-testid="checkout-email-input"
              className="w-full bg-white border border-[#4A3F35]/20 rounded-sm px-4 py-3 text-sm outline-none focus:border-[#C6A769] mb-6"
            />

            <button
              onClick={checkout}
              data-testid="checkout-button"
              className="w-full bg-[#4A3F35] text-[#FAF7F2] px-8 py-4 rounded-sm text-sm uppercase tracking-[0.15em] hover:bg-[#C6A769] transition-colors duration-500 inline-flex items-center justify-center gap-2"
            >
              Secure Checkout
            </button>
            <p className="text-xs text-[#8A7D71] text-center mt-4">
              Payments handled securely by Stripe.
            </p>
            <p data-testid="checkout-final-sale-note" className="text-xs text-[#8A7D71] text-center mt-2">
              All sales are final — digital products are delivered instantly.{" "}
              <Link to="/terms" data-testid="checkout-terms-link" className="text-[#C6A769] underline underline-offset-2 hover:text-[#4A3F35] transition-colors">
                Terms of Use
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
